import { Fingerprint, Cpu, ArrowRightLeft, CheckCircle2, Info } from "lucide-react";
import { PALETTE } from "../../styles/configStyles";
import FingerprintDetailsModal from "../modals/FingerprintDetailsModal";

const FingerprintTab = ({
  styles,
  t,
  theme,
  isRTL,
  language,
  config,
  setConfig,
  canEditField,
  getFieldStyle,
  onOpenFingerprintDetails,
}) => {
  const canEdit = canEditField("attendance");
  const currentMode = config.attendanceDetectionMode || "deviceName";
  const ft = t.fingerprint;

  const modes = [
    {
      id: "deviceName",
      label: ft.deviceName,
      desc: ft.deviceNameDesc,
      icon: Cpu,
      color: PALETTE.primary.base,
      gradient: `linear-gradient(135deg, ${PALETTE.primary.base}, ${PALETTE.primary.dark})`,
      details: language === "ar" 
        ? [
            "✓ يستخدم اسم جهاز البصمة لتحديد الحضور والانصراف",
            "✓ يتطلب جهازين منفصلين: واحد للحضور وآخر للانصراف",
            "✓ اسم الجهاز يجب أن يحتوي على كلمة 'check-in' للحضور",
            "✓ اسم الجهاز يجب أن يحتوي على كلمة 'check-out' للانصراف",
            "✓ مناسب للشركات التي تملك أجهزة بصمة منفصلة",
          ]
        : [
            "✓ Uses the fingerprint device name to determine check-in/check-out",
            "✓ Requires separate devices: one for check-in and one for check-out",
            "✓ Device name must contain 'check-in' for attendance",
            "✓ Device name must contain 'check-out' for departure",
            "✓ Best for companies with separate fingerprint devices",
          ],
    },
    {
      id: "sequential",
      label: ft.sequential,
      desc: ft.sequentialDesc,
      icon: ArrowRightLeft,
      color: PALETTE.emerald.base,
      gradient: `linear-gradient(135deg, ${PALETTE.emerald.base}, ${PALETTE.emerald.dark})`,
      details: language === "ar"
        ? [
            "✓ يحدد الحضور والانصراف بناءً على تسلسل الأحداث",
            "✓ يعمل مع جهاز واحد فقط - لا يتطلب أجهزة منفصلة",
            "✓ الخطوة الأولى تُعتبر حضور (check-in)",
            "✓ الخطوة الثانية تُعتبر انصراف (check-out)",
            "✓ إذا كان هناك نقرة واحدة فقط، يتم تحديدها بناءً على وقت الوردية",
          ]
        : [
            "✓ Determines check-in/check-out based on event sequence",
            "✓ Works with a single device - no need for separate devices",
            "✓ First fingerprint event is treated as check-in",
            "✓ Second fingerprint event is treated as check-out",
            "✓ Single events are determined based on shift timing",
          ],
    },
  ];

  return (
    <>
      <div style={{ marginTop: "24px", marginBottom: "24px", direction: isRTL ? "rtl" : "ltr" }}>
      {/* Section Title */}
      <h3 style={styles.sectionTitle}>
        <div style={styles.sectionIconBadge(PALETTE.primary.base)}>
          <Fingerprint size={20} />
        </div>
        {ft.title}
        {!canEdit && (
          <span
            style={{
              fontSize: "11px",
              fontWeight: "700",
              marginLeft: isRTL ? "0" : "12px",
              marginRight: isRTL ? "12px" : "0",
              padding: "4px 12px",
              borderRadius: "6px",
              background: `${PALETTE.amber.base}15`,
              color: PALETTE.amber.base,
              border: `1px solid ${PALETTE.amber.base}25`,
            }}
          >
            {language === "ar" ? "(للقراءة فقط)" : "(Read-only)"}
          </span>
        )}
      </h3>

      {/* Description */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: "10px",
          padding: "14px 16px",
          borderRadius: "12px",
          background:
            theme === "dark"
              ? "rgba(99,102,241,0.08)"
              : "rgba(99,102,241,0.04)",
          border: `1px solid ${theme === "dark"
              ? "rgba(99,102,241,0.2)"
              : "rgba(99,102,241,0.12)"
            }`,
          marginBottom: "24px",
          flexDirection: isRTL ? "row-reverse" : "row",
          direction: isRTL ? "ltr" : "ltr",
        }}
      >
        <Info
          size={18}
          style={{
            flexShrink: 0,
            marginTop: "2px",
            color: PALETTE.primary.base,

          }}
        />
        <p
          style={{
            margin: 0,
            fontSize: "13px",
            lineHeight: "1.6",
            color:
              theme === "dark" ? "rgba(226,232,240,0.85)" : "#475569",
            textAlign: isRTL ? "right" : "left",
          }}
        >
          {ft.detectionModeDesc}
        </p>
      </div>

      {/* Detection Mode Label */}
      <label
        style={{
          ...styles.label,
          marginBottom: "16px",
          display: "block",
          fontSize: "14px",
          fontWeight: 700,
          textAlign: isRTL ? "right" : "left",
        }}
      >
        {ft.detectionMode}
      </label>

      {/* Mode Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: "16px",
          marginBottom: "24px",
          direction: isRTL ? "rtl" : "ltr",
        }}
      >
        {modes.map((mode) => {
          const isActive = currentMode === mode.id;
          return (
            <button
              key={mode.id}
              onClick={() =>
                canEdit &&
                setConfig({ ...config, attendanceDetectionMode: mode.id })
              }
              disabled={!canEdit}
              style={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                alignItems: isRTL ? "flex-end" : "flex-start",
                textAlign: isRTL ? "right" : "left",
                padding: "24px",
                borderRadius: "16px",
                border: isActive
                  ? `2px solid ${mode.color}`
                  : `1px solid ${theme === "dark"
                    ? "rgba(148,163,184,0.2)"
                    : "rgba(226,232,240,0.8)"
                  }`,
                background: isActive
                  ? theme === "dark"
                    ? `${mode.color}10`
                    : `${mode.color}06`
                  : theme === "dark"
                    ? "rgba(30,41,59,0.6)"
                    : "rgba(255,255,255,0.8)",
                cursor: canEdit ? "pointer" : "not-allowed",
                opacity: canEdit ? 1 : 0.6,
                transition: "all 0.25s ease",
                boxShadow: isActive
                  ? `0 4px 20px ${mode.color}20`
                  : "0 2px 8px rgba(0,0,0,0.04)",
                outline: "none",
                direction: isRTL ? "rtl" : "ltr",
              }}
              onMouseEnter={(e) => {
                if (canEdit && !isActive) {
                  e.currentTarget.style.borderColor = `${mode.color}60`;
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = `0 6px 24px ${mode.color}15`;
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.currentTarget.style.borderColor =
                    theme === "dark"
                      ? "rgba(148,163,184,0.2)"
                      : "rgba(226,232,240,0.8)";
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 2px 8px rgba(0,0,0,0.04)";
                }
              }}
            >
              {/* Active badge */}
              {isActive && (
                <div
                  style={{
                    position: "absolute",
                    top: "12px",
                    [isRTL ? "left" : "right"]: "12px",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    padding: "4px 10px",
                    borderRadius: "20px",
                    background: mode.gradient,
                    color: "#fff",
                    fontSize: "11px",
                    fontWeight: 700,
                    direction: isRTL ? "rtl" : "ltr",
                  }}
                >
                  <CheckCircle2 size={12} />
                  {ft.currentMode}
                </div>
              )}

              {/* Icon */}
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "14px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: isActive
                    ? mode.gradient
                    : theme === "dark"
                      ? "rgba(148,163,184,0.12)"
                      : "rgba(241,245,249,0.8)",
                  color: isActive
                    ? "#fff"
                    : theme === "dark"
                      ? "rgba(148,163,184,0.7)"
                      : "#64748b",
                  marginBottom: "16px",
                  alignSelf: isRTL ? "flex-start" : "auto",
                  transition: "all 0.25s ease",
                  flexShrink: 0,
                }}
              >
                <mode.icon size={24} />
              </div>

              {/* Label with Info Icon */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  width: "100%",
                  marginBottom: "8px",
                  position: "relative",
                }}
              >
                <span
                  style={{
                    fontSize: "16px",
                    fontWeight: 700,
                    color: isActive
                      ? mode.color
                      : theme === "dark"
                        ? "#f1f5f9"
                        : "#0f172a",
                    flex: 1,
                  }}
                >
                  {mode.label}
                </span>

                {/* Info Icon */}
                <button
                  type="button"
                  style={{
                    background: "none",
                    border: "none",
                    padding: "4px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenFingerprintDetails(mode);
                  }}
                  title={language === "ar" ? "اضغط للمزيد من التفاصيل" : "Click for details"}
                >
                  <Info
                    size={16}
                    style={{
                      color: isActive ? mode.color : "#94a3b8",
                      transition: "color 0.2s ease",
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = mode.color;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = isActive ? mode.color : "#94a3b8";
                    }}
                  />
                </button>
              </div>

              {/* Description */}
              <span
                style={{
                  fontSize: "13px",
                  lineHeight: "1.5",
                  color:
                    theme === "dark"
                      ? "rgba(226,232,240,0.65)"
                      : "#64748b",
                  width: "100%",
                  display: "block",
                }}
              >
                {mode.desc}
              </span>
            </button>
          );
        })}
      </div>

    </div>
    </>
  );
};

export default FingerprintTab;
